import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { MapPinIcon, XCircleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { API_BASE_URL } from '../config/environment';

/**
 * Google Places Autocomplete Component with Backend Proxy
 *
 * Security:
 * - API key hidden on backend (not exposed to browser)
 * - All requests proxied through backend API
 *
 * Features:
 * - Debouncing (500ms) to avoid excessive API calls
 * - Minimum 3 characters before search
 * - Client-side caching (1 hour TTL)
 * - Session token for billing optimization
 * - Rate limiting (handled by backend)
 * - Graceful fallback if API unavailable
 *
 * Cost Optimization:
 * - Uses Autocomplete API: $2.83 per 1000 requests (cheaper than Places API)
 * - Session tokens group multiple requests into single billable session
 * - Caching reduces duplicate requests
 * - Debouncing reduces total requests by ~70%
 */

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: ${props => props.$glassTheme ? '0.25rem 2rem 0.25rem 0.5rem' : '0.875rem 2.5rem 0.875rem 1rem'};
  font-size: ${props => props.$glassTheme ? 'inherit' : '1rem'};
  font-weight: ${props => props.$glassTheme ? '600' : 'normal'};
  border: ${props => props.$glassTheme ? 'none' : '2px solid #e0e0e0'};
  border-bottom: ${props => props.$glassTheme ? '2px solid rgba(255,255,255,0.5)' : undefined};
  border-radius: ${props => props.$glassTheme ? '0' : '12px'};
  outline: none;
  transition: all 0.2s;
  font-family: inherit;
  background: ${props => props.$glassTheme ? 'transparent' : 'white'};
  color: ${props => props.$glassTheme ? 'white' : 'inherit'};

  &:focus {
    border-color: ${props => props.$glassTheme ? '#4285F4' : '#FF385C'};
    border-bottom-color: ${props => props.$glassTheme ? '#4285F4' : undefined};
    box-shadow: ${props => props.$glassTheme ? 'none' : '0 0 0 3px rgba(255, 56, 92, 0.1)'};
  }

  &::placeholder {
    color: ${props => props.$glassTheme ? 'rgba(255,255,255,0.4)' : '#999'};
  }

  ${props => props.$hasError && `
    border-color: #ef4444;
    &:focus {
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }
  `}
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  transition: color 0.2s;

  &:hover {
    color: #666;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SuggestionsContainer = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.05),
    0 10px 25px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  border: 1px solid #e0e0e0;
`;

const SuggestionItem = styled.div`
  padding: 0.875rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  transition: background-color 0.15s;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f9f9f9;
  }

  svg {
    width: 18px;
    height: 18px;
    color: #4285F4;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const SuggestionText = styled.div`
  flex: 1;
`;

const SuggestionMain = styled.div`
  font-size: 0.938rem;
  font-weight: 500;
  color: #222;
  margin-bottom: 2px;
`;

const SuggestionSecondary = styled.div`
  font-size: 0.813rem;
  color: #666;
`;

const LoadingState = styled.div`
  padding: 1rem;
  text-align: center;
  color: #999;
  font-size: 0.875rem;
`;

const ErrorState = styled.div`
  padding: 1rem;
  text-align: center;
  color: #ef4444;
  font-size: 0.875rem;
`;

const NoResults = styled.div`
  padding: 1rem;
  text-align: center;
  color: #666;
  font-size: 0.875rem;
`;

// Cache management
const CACHE_KEY_PREFIX = 'place_autocomplete_';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

class PlaceCache {
  static set(query, predictions) {
    const cacheData = {
      predictions,
      timestamp: Date.now()
    };
    try {
      localStorage.setItem(CACHE_KEY_PREFIX + query, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Failed to cache predictions:', e);
    }
  }

  static get(query) {
    try {
      const cached = localStorage.getItem(CACHE_KEY_PREFIX + query);
      if (!cached) return null;

      const { predictions, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (age > CACHE_TTL) {
        localStorage.removeItem(CACHE_KEY_PREFIX + query);
        return null;
      }

      return predictions;
    } catch (e) {
      console.warn('Failed to retrieve cached predictions:', e);
      return null;
    }
  }

  static clear() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('Failed to clear cache:', e);
    }
  }
}

// Rate limiting
class RateLimiter {
  constructor(maxRequests, timeWindow) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow; // in milliseconds
    this.requests = [];
  }

  canMakeRequest() {
    const now = Date.now();
    // Remove requests outside the time window
    this.requests = this.requests.filter(time => now - time < this.timeWindow);

    if (this.requests.length >= this.maxRequests) {
      return false;
    }

    this.requests.push(now);
    return true;
  }

  getRemainingTime() {
    if (this.requests.length < this.maxRequests) {
      return 0;
    }
    const oldestRequest = this.requests[0];
    const timeElapsed = Date.now() - oldestRequest;
    return Math.max(0, this.timeWindow - timeElapsed);
  }
}

// Single rate limiter instance (10 requests per minute)
const rateLimiter = new RateLimiter(10, 60 * 1000);

const PlaceAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "City, country, or landmark...",
  className,
  error,
  glassTheme = false
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [sessionToken, setSessionToken] = useState(() => generateSessionToken());

  const debounceTimerRef = useRef(null);
  const containerRef = useRef(null);

  // Generate a simple session token for billing optimization
  function generateSessionToken() {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  }

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync with external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value, inputValue]);

  const fetchPredictions = useCallback(async (query) => {
    // Check cache first
    const cached = PlaceCache.get(query);
    if (cached) {
      setSuggestions(cached);
      setShowSuggestions(true);
      setIsLoading(false);
      return;
    }

    // Check rate limit
    if (!rateLimiter.canMakeRequest()) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime() / 1000);
      setErrorMessage(`Rate limit exceeded. Please wait ${remainingTime}s`);
      setIsLoading(false);
      setTimeout(() => setErrorMessage(null), remainingTime * 1000);
      return;
    }

    try {
      // Call backend API instead of Google Maps directly
      const response = await axios.post(`${API_BASE_URL}/v1/google-maps/places/autocomplete`, {
        input: query,
        // types: '(cities)', // Removed - causes INVALID_REQUEST error
        sessionToken: sessionToken
      });

      setIsLoading(false);

      if (response.data && response.data.status === 'OK' && response.data.predictions) {
        const formattedPredictions = response.data.predictions.map(p => ({
          placeId: p.placeId,
          description: p.description,
          mainText: p.mainText,
          secondaryText: p.secondaryText
        }));

        setSuggestions(formattedPredictions);
        setShowSuggestions(true);
        setErrorMessage(null);

        // Cache the results
        PlaceCache.set(query, formattedPredictions);
      } else if (response.data && response.data.status === 'ZERO_RESULTS') {
        setSuggestions([]);
        setShowSuggestions(true);
        setErrorMessage(null);
      } else {
        setErrorMessage('No results found');
        setSuggestions([]);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching autocomplete suggestions:', error);

      if (error.response?.status === 503) {
        setErrorMessage('Service temporarily unavailable');
      } else if (error.response?.status === 429) {
        setErrorMessage('Too many requests. Please wait a moment.');
      } else {
        setErrorMessage('Failed to fetch suggestions');
      }
      setSuggestions([]);
    }
  }, [sessionToken]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Notify parent of change
    if (onChange) {
      onChange(newValue);
    }

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Hide suggestions if input is too short
    if (newValue.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setErrorMessage(null);
      return;
    }

    // Show loading state
    setIsLoading(true);
    setErrorMessage(null);

    // Debounce API call (500ms)
    debounceTimerRef.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, 500);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.description);
    setShowSuggestions(false);
    setSuggestions([]);

    // Create new session token for next search
    setSessionToken(generateSessionToken());

    // Notify parent
    if (onSelect) {
      onSelect({
        name: suggestion.description,
        placeId: suggestion.placeId,
        mainText: suggestion.mainText,
        secondaryText: suggestion.secondaryText
      });
    }
    if (onChange) {
      onChange(suggestion.description);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    setErrorMessage(null);

    if (onChange) {
      onChange('');
    }
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <AutocompleteContainer ref={containerRef} className={className}>
      <InputWrapper>
        <StyledInput
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          $hasError={!!error}
          $glassTheme={glassTheme}
          autoComplete="off"
        />
        {inputValue && (
          <ClearButton onClick={handleClear} type="button" aria-label="Clear input">
            <XCircleIcon />
          </ClearButton>
        )}
      </InputWrapper>

      {showSuggestions && (
        <SuggestionsContainer>
          {isLoading ? (
            <LoadingState>Searching places...</LoadingState>
          ) : errorMessage ? (
            <ErrorState>{errorMessage}</ErrorState>
          ) : suggestions.length > 0 ? (
            suggestions.map((suggestion) => (
              <SuggestionItem
                key={suggestion.placeId}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <MapPinIcon />
                <SuggestionText>
                  <SuggestionMain>{suggestion.mainText}</SuggestionMain>
                  {suggestion.secondaryText && (
                    <SuggestionSecondary>{suggestion.secondaryText}</SuggestionSecondary>
                  )}
                </SuggestionText>
              </SuggestionItem>
            ))
          ) : (
            <NoResults>No places found. Try a different search.</NoResults>
          )}
        </SuggestionsContainer>
      )}
    </AutocompleteContainer>
  );
};

export default PlaceAutocomplete;
